import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Loader2,
  Merge,
  X,
  CheckCircle2,
  MapPin,
  History,
  Link2,
} from "lucide-react";
import { Button } from "../../components/ui/Button";
import { Pagination } from "../../components/ui/Pagination";
import { Badge } from "../../components/ui/Badge";
import { Modal, ModalContent, ModalFooter } from "../../components/ui/Modal";
import { FlaggerMultiSelect, useFlaggerFilter } from "../../components/ui/FlaggerMultiSelect";
import {
  ComparisonGrid,
  DuplicateReviewCard,
  MergeResolutionBadge,
  MergedDuplicateCard,
  ResolutionDetails,
  ReviewEventLink,
  formatReviewDateTime,
} from "../../components/ui/DuplicateReview";
import { CitySelect } from "../../components/ui/CitySelect";
import { SearchFilterForm, useSearchFilter } from "../../components/ui/SearchFilterForm";
import { useReviewQueueCount } from "../../hooks/useReviewQueueCount";
import LinkRoomsDialog from "./LinkRoomsDialog";
import { venuesApi } from "../../utils/api";
import { capitalize, venueDisplayName } from "../../utils/helpers";

const KIND_LABEL = {
  name_and_location: "Same name and location",
  name_only: "Same name",
  location_only: "Same location",
};

function formatDistance(meters) {
  if (meters == null) return "no coordinates on both venues";
  return meters >= 1000 ? `≈ ${(meters / 1000).toFixed(1)} km apart` : `≈ ${Math.round(meters)} m apart`;
}

function VenueCard({ venue, isSelected, onSelect, selectLabel, conflictingProviders = [] }) {
  const sourceIds = venue.source_ids || {};
  // `names` is every name the venue has been seen under; `name` is the last one seen. When an
  // admin set a preferred name the heading shows that instead, so the provider's own name joins
  // the chips below rather than disappearing.
  const displayName = venueDisplayName(venue);
  const previousNames = [...new Set([venue.name, ...(venue.names || [])])].filter(
    (n) => n && n !== displayName
  );
  // `events` is a preview of the latest-dated ones; `event_count` is the exact total.
  const events = venue.events || [];
  const hiddenEventCount = Math.max((venue.event_count ?? 0) - events.length, 0);
  const hasCoordinates = venue.latitude != null && venue.longitude != null;

  return (
    <div
      className={`min-w-0 rounded-lg border p-4 transition-colors ${
        isSelected ? "border-primary bg-primary/5" : "border-border bg-card"
      }`}
    >
      <div className="mb-2 flex items-center gap-2">
        <MapPin className="h-4 w-4 shrink-0 text-muted-foreground" />
        {/* The admin-set name wins here too — it is one of the names detection compared, so a
            pair flagged only because of it would otherwise look unexplained. */}
        <h4 className="min-w-0 flex-1 truncate text-sm font-semibold">{displayName}</h4>
        {venue.verified && (
          <Badge variant="default" className="shrink-0 text-[10px]">verified</Badge>
        )}
      </div>

      {previousNames.length > 0 && (
        <div className="mb-2 flex flex-wrap items-center gap-1">
          <span className="text-[10px] uppercase tracking-wide text-muted-foreground">also known as</span>
          {previousNames.map((name) => (
            <span key={name} className="rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">
              {name}
            </span>
          ))}
        </div>
      )}

      <div className="space-y-1.5 text-xs text-muted-foreground">
        {Object.keys(sourceIds).length > 0 && (
          <div className="flex flex-wrap gap-1">
            {Object.entries(sourceIds).map(([provider, value]) => (
              <span
                key={provider}
                className={`rounded px-1.5 py-0.5 text-[10px] ${
                  conflictingProviders.includes(provider)
                    ? "bg-yellow-100 text-yellow-800"
                    : "bg-muted"
                }`}
                title={
                  conflictingProviders.includes(provider)
                    ? `Both venues carry a different ${provider} id — merging keeps the canonical one`
                    : undefined
                }
              >
                {provider}: <span className="font-mono">{String(value).slice(0, 16)}</span>
              </span>
            ))}
          </div>
        )}
        <p className="truncate">{venue.address || "No address"}</p>
        <p className="truncate">
          {[venue.city, venue.state, venue.country].filter(Boolean).join(", ") || "No locality"}
        </p>
        {/* Always rendered, including when unassigned: a merge absorbs `main_population` into a
            canonical that lacks one, so the admin has to see which value each choice would keep. */}
        <p className="truncate">
          Main population:{" "}
          {venue.main_population ? (
            capitalize(venue.main_population)
          ) : (
            <span className="italic">Unassigned</span>
          )}
        </p>
        <p className="font-mono text-[11px]">
          {hasCoordinates ? `${venue.latitude}, ${venue.longitude}` : "No coordinates"}
        </p>
        {venue.capacity != null && <p>Capacity: {venue.capacity}</p>}
      </div>

      <div className="mt-3 border-t border-border pt-2">
        <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          Events ({venue.event_count ?? 0})
        </p>
        {events.length === 0 ? (
          <p className="text-xs text-muted-foreground">No referenced events</p>
        ) : (
          <ul className="max-h-48 space-y-1.5 overflow-y-auto text-xs">
            {events.map((ev) => (
              <ReviewEventLink key={ev.id || ev.title} event={ev} />
            ))}
            {hiddenEventCount > 0 && (
              <li className="text-[10px] italic text-muted-foreground">+{hiddenEventCount} more not shown</li>
            )}
          </ul>
        )}
      </div>

      {onSelect && (
        <Button
          variant={isSelected ? "default" : "outline"}
          size="sm"
          className="mt-3 w-full text-xs"
          onClick={() => onSelect(venue.id)}
        >
          {isSelected && <CheckCircle2 className="mr-1 h-3.5 w-3.5" />}
          {selectLabel}
        </Button>
      )}
    </div>
  );
}

function VenuePair({ group, onActionSuccess }) {
  const [mergeModalOpen, setMergeModalOpen] = useState(false);
  const [canonicalId, setCanonicalId] = useState(group.canonical_hint_id || null);
  const [mergeLoading, setMergeLoading] = useState(false);
  const [mergeError, setMergeError] = useState(null);
  const [markLoading, setMarkLoading] = useState(false);
  const [markError, setMarkError] = useState(null);
  const [linkOpen, setLinkOpen] = useState(false);

  const [venueA, venueB] = group.venues || [];
  if (!venueA || !venueB) return null;

  const conflictingProviders = Object.keys(venueA.source_ids || {}).filter(
    (provider) =>
      (venueB.source_ids || {})[provider] !== undefined &&
      venueB.source_ids[provider] !== venueA.source_ids[provider],
  );

  const openMergeModal = () => {
    setCanonicalId(group.canonical_hint_id || venueB.id);
    setMergeError(null);
    setMergeModalOpen(true);
  };

  const handleConfirmMerge = async () => {
    const sourceId = group.venues.find((v) => v.id !== canonicalId)?.id;
    if (!sourceId || !canonicalId) return;
    setMergeLoading(true);
    setMergeError(null);
    try {
      const result = await venuesApi.mergeInto(sourceId, canonicalId);
      setMergeModalOpen(false);
      await onActionSuccess?.(
        result?.room_group_warning ? `Venues merged. Warning: ${result.room_group_warning}` : "Venues merged",
      );
    } catch (e) {
      setMergeError(e?.response?.data?.detail || "Merge failed");
    } finally {
      setMergeLoading(false);
    }
  };

  const handleMarkDifferent = async () => {
    // Resolve the pair as "different" — these are not the same venue. A resolved pair is never
    // re-flagged by future detection runs.
    setMarkLoading(true);
    setMarkError(null);
    try {
      await venuesApi.markDifferent(venueA.id, venueB.id);
      await onActionSuccess?.("Marked as different");
    } catch (e) {
      setMarkError(e?.response?.data?.detail || "Could not mark as different");
    } finally {
      setMarkLoading(false);
    }
  };

  return (
    <DuplicateReviewCard
      title={
        <>
          {venueDisplayName(venueA)} <span className="font-normal text-muted-foreground">vs</span>{" "}
          {venueDisplayName(venueB)}
          {group.kind && (
            <Badge variant="secondary" className="ml-2 align-middle text-[10px]">
              {KIND_LABEL[group.kind] || group.kind}
            </Badge>
          )}
          <span className="ml-2 align-middle text-[11px] font-normal text-muted-foreground">
            {formatDistance(group.distance_meters)}
          </span>
        </>
      }
      reason={group.reason}
      score={group.similarity_score}
      scoreDestructiveAt={95}
      scoreWarningAt={85}
    >
      <ComparisonGrid
        left={<VenueCard venue={venueA} conflictingProviders={conflictingProviders} />}
        right={<VenueCard venue={venueB} conflictingProviders={conflictingProviders} />}
      />

          <div className="flex flex-col gap-2 pt-1 sm:flex-row">
            <Button variant="outline" size="sm" className="gap-1.5" onClick={openMergeModal}>
              <Merge className="h-3.5 w-3.5" />
              Merge (same venue)
            </Button>
            <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setLinkOpen(true)}>
              <Link2 className="h-3.5 w-3.5" />
              Rooms of the same venue
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="gap-1.5 text-muted-foreground sm:ml-auto"
              onClick={handleMarkDifferent}
              disabled={markLoading}
            >
              {markLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <X className="h-3.5 w-3.5" />}
              Different (not the same venue)
            </Button>
          </div>
          {markError && <p className="text-sm text-destructive">{markError}</p>}

      <LinkRoomsDialog
        open={linkOpen}
        venueA={venueA}
        venueB={venueB}
        onClose={() => setLinkOpen(false)}
        onLinked={async () => {
          setLinkOpen(false);
          await onActionSuccess?.("Linked as rooms");
        }}
      />

      <Modal open={mergeModalOpen} onOpenChange={setMergeModalOpen}>
        <ModalContent
          title="Merge venues — pick the canonical one"
          description="The canonical venue stays active and absorbs the other's source ids, name history, and any field it is missing. Every event moves to the canonical venue, and the other venue is kept as a duplicate record pointing at it. Its name history is preserved, so feeds still using the old name keep resolving here."
        >
          <div className="mt-2 grid grid-cols-1 items-stretch gap-4 sm:grid-cols-2">
            <VenueCard
              venue={venueA}
              isSelected={canonicalId === venueA.id}
              onSelect={setCanonicalId}
              selectLabel="Keep this one (canonical)"
              conflictingProviders={conflictingProviders}
            />
            <VenueCard
              venue={venueB}
              isSelected={canonicalId === venueB.id}
              onSelect={setCanonicalId}
              selectLabel="Keep this one (canonical)"
              conflictingProviders={conflictingProviders}
            />
          </div>
          {mergeError && <p className="mt-3 text-sm text-destructive">{mergeError}</p>}
          <ModalFooter className="mt-4">
            <Button variant="outline" onClick={() => setMergeModalOpen(false)} disabled={mergeLoading}>
              Cancel
            </Button>
            <Button onClick={handleConfirmMerge} disabled={!canonicalId || mergeLoading}>
              {mergeLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Merge className="mr-2 h-4 w-4" />}
              Confirm merge
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </DuplicateReviewCard>
  );
}

const MERGE_TYPE_VARIANT = { admin_review: "default" };
const MERGE_TYPE_LABEL = { admin_review: "Admin merge" };

function VenueResolutionDetails({ resolution }) {
  const conflicts = resolution?.source_id_conflicts || [];
  return (
    <ResolutionDetails
      resolution={resolution}
      mergeTypeLabels={MERGE_TYPE_LABEL}
      mergeTypeVariants={MERGE_TYPE_VARIANT}
    >
      {conflicts.length > 0 && <span>Source id conflicts kept from canonical: {conflicts.join(", ")}</span>}
    </ResolutionDetails>
  );
}

function MergedVenuePair({ pair }) {
  const duplicate = pair.duplicate_venue;
  const canonical = pair.canonical_venue;
  const missingCanonical = (
    <div className="flex min-w-0 items-center justify-center rounded-lg border border-dashed border-border bg-muted/20 p-4 text-xs italic text-muted-foreground">
      Canonical venue no longer exists
    </div>
  );

  return (
    <MergedDuplicateCard
      icon={<Merge className="h-4 w-4 shrink-0 text-muted-foreground" />}
      title={
        <>
          <span className="text-red-700">{duplicate?.name || duplicate?.id}</span>
          <span className="mx-1.5 font-normal text-muted-foreground">→</span>
          <span>{canonical?.name || canonical?.id || "missing canonical"}</span>
        </>
      }
      meta={
        <>
          <MergeResolutionBadge resolution={pair.resolution} />
          {pair.resolution?.resolved_at && (
            <span className="text-xs text-muted-foreground">{formatReviewDateTime(pair.resolution.resolved_at)}</span>
          )}
        </>
      }
    >
      <VenueResolutionDetails resolution={pair.resolution} />
      <ComparisonGrid left={<VenueCard venue={duplicate} />} right={canonical ? <VenueCard venue={canonical} /> : missingCanonical} separator="→" />
    </MergedDuplicateCard>
  );
}

function MergedVenuesPanel() {
  const [pairs, setPairs] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, page_size: 20, total: 0, total_pages: 0 });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { searchInput, setSearchInput, search, submitSearch, clearSearch } = useSearchFilter();
  const {
    flaggers,
    selectedFlaggerIds: flaggedBySelection,
    setSelectedFlaggerIds: setFlaggedBySelection,
    flaggedByParam,
    syncFlaggers,
  } = useFlaggerFilter();

  const load = useCallback(async (pageNum = 1, searchTerm = "", flaggedBy = "") => {
    setLoading(true);
    setError(null);
    try {
      const data = await venuesApi.getFlaggedDuplicates(pageNum, 20, searchTerm || null, flaggedBy || null);
      setPairs(data.pairs || []);
      setPagination(data.pagination || { page: 1, page_size: 20, total: 0, total_pages: 0 });
      syncFlaggers(data.flaggers);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load merged venues.");
    } finally {
      setLoading(false);
    }
  }, [syncFlaggers]);

  useEffect(() => {
    if (flaggedByParam === null) {
      setPairs([]);
      setPagination({ page: 1, page_size: 20, total: 0, total_pages: 1 });
      setLoading(false);
      return;
    }
    load(page, search, flaggedByParam);
  }, [load, page, search, flaggedByParam]);

  const handleSearchSubmit = (e) => {
    setPage(1);
    submitSearch(e);
  };

  const handleSearchClear = () => {
    setPage(1);
    clearSearch();
  };

  const handleFlaggedByChange = (next) => {
    setPage(1);
    setFlaggedBySelection(next);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <h2 className="text-lg font-semibold">Merged venues</h2>
          <p className="text-sm text-muted-foreground">
            Venues retained as duplicates after their events and missing fields were absorbed into
            the canonical venue.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2 sm:shrink-0">
          <SearchFilterForm
            value={searchInput}
            onChange={setSearchInput}
            onSubmit={handleSearchSubmit}
            onClear={handleSearchClear}
            placeholder="Search by venue name..."
            loading={loading}
            submitVariant="default"
            clearContent="Clear"
            inputClassName="sm:w-56"
          >
            <FlaggerMultiSelect
              flaggers={flaggers}
              selected={flaggedBySelection}
              onChange={handleFlaggedByChange}
            />
          </SearchFilterForm>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {error}
        </div>
      )}

      {!loading && (
        <>
          <div className="text-sm text-muted-foreground">
            {pagination.total === 0
              ? "No venues marked as duplicates"
              : `${pagination.total} venue${pagination.total !== 1 ? "s" : ""} marked as duplicate`}
            {search && <span> matching &ldquo;{search}&rdquo;</span>}
            {flaggedByParam === null && <span> · no flaggers selected</span>}
            {flaggedByParam && (
              <span>
                {" · flagged by "}
                {flaggedBySelection.length === 1
                  ? flaggers.find((f) => f.id === flaggedBySelection[0])?.label || flaggedBySelection[0]
                  : `${flaggedBySelection.length} selected`}
              </span>
            )}
          </div>

          {pairs.length > 0 ? (
            <div className="space-y-3">
              {pairs.map((pair) => (
                <MergedVenuePair key={pair.duplicate_venue?.id} pair={pair} />
              ))}
            </div>
          ) : (
            <div className="rounded-xl border border-dashed border-border py-16 text-center text-muted-foreground">
              <History className="mx-auto mb-3 h-8 w-8" />
              <p className="font-medium">No merged venues yet</p>
              <p className="mt-1 text-sm">Duplicate venue records will appear here.</p>
            </div>
          )}

          <Pagination page={page} totalPages={pagination.total_pages} onPageChange={setPage} />
        </>
      )}
    </div>
  );
}

export default function VenueDuplicatesView({ onQueueChange }) {
  const [view, setView] = useState("pairs");
  const [groups, setGroups] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, page_size: 20, total: 0, total_pages: 0 });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [scanned, setScanned] = useState(false);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  const [cityFilter, setCityFilter] = useState("");
  const { searchInput, setSearchInput, search, submitSearch, clearSearch } = useSearchFilter();
  // Changing a filter is one click on the city dropdown, so two loads are routinely in flight at
  // once — only the newest may render. (The badge has its own claim; this guards the list.)
  const duplicateRequestRef = useRef(0);
  const fetchQueueCount = useCallback(() => venuesApi.getDuplicates(1, 1), []);
  const { claim: claimQueueCount, refresh: refreshQueueCount } = useReviewQueueCount(fetchQueueCount, onQueueChange);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 4000);
  };

  const loadDuplicates = useCallback(
    async (pageNum = 1, searchTerm = "", city = "") => {
      const requestId = ++duplicateRequestRef.current;
      // Claim the badge slot before the request, so a slow earlier load can't overwrite a
      // newer count. A filtered load never publishes: `total_groups` is filtered too.
      const publishCount = searchTerm || city ? null : claimQueueCount();
      setLoading(true);
      setError(null);
      setScanned(false);
      try {
        const data = await venuesApi.getDuplicates(pageNum, 20, searchTerm || null, city || null);
        if (requestId !== duplicateRequestRef.current) return;

        const nextPagination = data.pagination || { page: 1, page_size: 20, total: 0, total_pages: 0 };
        setGroups(data.groups || []);
        setPagination(nextPagination);
        publishCount?.(data.total_groups);
        setScanned(true);

        // Resolving the last pair on a page strands the admin past the end of the queue,
        // where the "All clear" empty state claims there is nothing left to review.
        if (pageNum > 1 && pageNum > (nextPagination.total_pages || 1)) {
          setPage(Math.max(1, nextPagination.total_pages || 1));
        }
      } catch (e) {
        if (requestId !== duplicateRequestRef.current) return;
        setError(e?.response?.data?.detail || "Could not load venue duplicate pairs.");
      } finally {
        if (requestId === duplicateRequestRef.current) setLoading(false);
      }
    },
    [claimQueueCount]
  );

  // A load settling after unmount must not render; bumping the ref invalidates every in-flight one.
  useEffect(() => () => {
    duplicateRequestRef.current += 1;
  }, []);

  useEffect(() => {
    loadDuplicates(page, search, cityFilter);
    // No `refreshSignal` prop here, unlike the artist view: venues have no second flow (the
    // artists' MusicBrainz-ID change) that resolves a pair from outside this component.
  }, [loadDuplicates, page, search, cityFilter]);

  const handleSearchSubmit = (e) => {
    setPage(1);
    submitSearch(e);
  };

  const handleSearchClear = () => {
    setPage(1);
    clearSearch();
  };

  const handleCityChange = (city) => {
    setPage(1);
    setCityFilter(city);
  };

  return (
    <div className="space-y-6">
      <div className="inline-flex rounded-lg border border-border p-0.5">
        <button
          type="button"
          onClick={() => setView("pairs")}
          className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
            view === "pairs" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
          }`}
        >
          Open pairs
        </button>
        <button
          type="button"
          onClick={() => setView("history")}
          className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
            view === "history" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
          }`}
        >
          <History className="h-3.5 w-3.5" />
          Merged venues
        </button>
      </div>

      {view === "history" ? (
        <MergedVenuesPanel />
      ) : (
        <>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <h2 className="text-lg font-semibold">Possible duplicate venues</h2>
          <p className="text-sm text-muted-foreground">
            Same-name pairs with an unverified location, and same-location pairs under a different
            name. The suggested canonical prefers a verified venue, else the one with more events.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2 sm:shrink-0">
          <CitySelect value={cityFilter} onChange={handleCityChange} />
          <SearchFilterForm
            value={searchInput}
            onChange={setSearchInput}
            onSubmit={handleSearchSubmit}
            onClear={handleSearchClear}
            placeholder="Search by venue name..."
            loading={loading}
            submitVariant="default"
            clearContent="Clear"
            inputClassName="sm:w-56"
          />
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {error}
        </div>
      )}

      {toast && (
        <div className="flex items-center gap-2 rounded-lg border border-green-300 bg-green-50 px-4 py-3 text-sm text-green-800">
          <CheckCircle2 className="h-4 w-4 shrink-0" />
          {toast}
        </div>
      )}

      {loading && (
        <div className="rounded-xl border border-dashed border-border py-16 text-center text-muted-foreground">
          <Loader2 className="mx-auto mb-3 h-8 w-8 animate-spin" />
          <p className="font-medium">Loading venue duplicate pairs…</p>
        </div>
      )}

      {!loading && scanned && (
        <>
          <div className="text-sm text-muted-foreground">
            {groups.length === 0
              ? "No venue duplicate pairs stored"
              : `${groups.length} pair${groups.length !== 1 ? "s" : ""} on this page · ${pagination.total} total`}
          </div>

          {groups.length > 0 && (
            <div className="space-y-3">
              {groups.map((group) => (
                <VenuePair
                  key={group.id}
                  group={group}
                  onActionSuccess={(msg) => {
                    showToast(msg);
                    loadDuplicates(page, search, cityFilter);
                    // Re-read the unfiltered count: decrementing `pagination.total` would
                    // publish a filtered number as the app-wide badge.
                    refreshQueueCount();
                  }}
                />
              ))}
            </div>
          )}

          <Pagination page={page} totalPages={pagination.total_pages} onPageChange={setPage} />

          {groups.length === 0 && (
            <div className="rounded-xl border border-dashed border-border py-16 text-center text-muted-foreground">
              <CheckCircle2 className="mx-auto mb-3 h-8 w-8 text-green-500" />
              <p className="font-medium">All clear</p>
              <p className="mt-1 text-sm">No possible duplicate venues detected.</p>
            </div>
          )}
        </>
      )}
        </>
      )}
    </div>
  );
}
